import type { NoteEntry, SubjectRecord } from '@ni/shared';
import { READING_PASTE_LABEL, pasteReadingDetail, pieceFailedLabel } from './copy';
import { CLAUSE_SEPARATOR, formatClock, keepLines } from './format';

/**
 * **What the record says about its own reading, read off the record.**
 *
 * A note is saved the moment it is posted and read afterwards, so between those
 * two things the office is looking at a person whose page does not yet carry
 * what they just typed. The page has to say so — and it has to say so out of the
 * *server's* answer, not out of anything the browser is holding.
 *
 * That is the whole point of this file. The old banner said "Retrying…" out of a
 * React mutation's `isPending`, which meant a refresh mid-retry showed a plain
 * failure with no sign that anything was under way; and it rendered
 * `extraction_failures[0]` with no excerpt, so a record with one failed note and
 * one good one printed the good note's facts under a sentence saying the notes
 * could not be read. Both of those are the same mistake: state the reader can
 * see that is not in the payload. Everything here is a pure function of
 * `SubjectRecord`, so a refresh and a poll and a mutation's invalidation all
 * land on exactly the same words.
 */

/** One row of the record's reading banner. */
export interface ReadingLine {
  /** The `prose_input` the row is about — the key, and what Retry posts to. */
  input_id: string;
  /** `reading` is work under way; `failed` is work that stopped. */
  tone: 'reading' | 'failed';
  /** The row's kicker. */
  label: string;
  /** The clause under the note: when it started, or why it stopped. Empty when there is nothing to say. */
  detail: string;
  /** The note itself, quoted, so a row names which note it is about. */
  note: string;
  /** Only a stopped reading can be started again. */
  canRetry: boolean;
}

/** How much of a note the banner quotes. */
const EXCERPT_CHARS = 80;

/**
 * The first stretch of a note, on one line and in quotes.
 *
 * Whitespace is collapsed before the cut: notes arrive with the line breaks the
 * office typed, and a banner row is one line — a raw newline inside the quotes
 * would open a gap in the middle of the sentence that names the note. The cut is
 * by characters rather than words on purpose: it is an identifier, not a
 * summary, and a word-safe cut makes two notes with the same opening clause
 * quote identically.
 *
 * The server sends its own excerpt on a failure (`ExtractionFailureEntry.excerpt`);
 * this is for the pending rows, which are read off the note itself.
 */
export function noteExcerpt(prose: string): string {
  const flat = prose.replace(/\s+/g, ' ').trim();
  if (flat.length <= EXCERPT_CHARS) return flat;
  return `${flat.slice(0, EXCERPT_CHARS).trimEnd()}…`;
}

/** The excerpt in the house's curly quotes. */
function quoted(excerpt: string): string {
  return `“${excerpt}”`;
}

/**
 * The kind, said only when it is worth saying. `intake` and `note` are what
 * nearly every row is — the note a person was entered with, and a note somebody
 * added — and naming them on every line would be a column of the same word.
 * `reference` and the rest are worth a clause.
 */
function kindClause(kind: NoteEntry['kind']): string | null {
  return kind === 'intake' || kind === 'note' ? null : kind;
}

function noteLine(excerpt: string, kind: NoteEntry['kind']): string {
  return keepLines([quoted(excerpt), kindClause(kind)]).join(CLAUSE_SEPARATOR);
}

/** The sentence a failure carries when the reader recorded none. */
const NO_REASON = 'No reason was recorded.';

/**
 * **Poll while anything is being read.**
 *
 * The answer decides the interval, so the poll that brings back the finished
 * note is the poll that stops the timer: nothing has to remember that a read was
 * under way, and a page opened on a record that is already mid-read starts
 * polling without being told.
 */
export function shouldPollExtraction(record: SubjectRecord | undefined): boolean {
  if (!record) return false;
  return record.notes.some((note) => note.extraction_status === 'pending');
}

/**
 * Oldest first, and a paste's pieces in the order they were pasted.
 *
 * `created_at` is the transaction's start on the server, so the rows of one
 * paste carry the **same** stamp to the millisecond — without the sequence, a
 * group's pieces sort by nothing at all and which of them the banner treats as
 * the paste's opening is whatever order the payload happened to arrive in.
 */
const byWritten = (
  a: { created_at: string; piece_seq: number | null },
  b: { created_at: string; piece_seq: number | null },
): number => {
  if (a.created_at !== b.created_at) return a.created_at < b.created_at ? -1 : 1;
  return (a.piece_seq ?? 0) - (b.piece_seq ?? 0);
};

/** Every row of a paste, whatever its reading is doing, keyed by the group. */
function piecesByGroup(notes: readonly NoteEntry[]): Map<string, NoteEntry[]> {
  const groups = new Map<string, NoteEntry[]>();
  for (const note of notes) {
    if (note.split_group_id === null) continue;
    const group = groups.get(note.split_group_id);
    if (group) group.push(note);
    else groups.set(note.split_group_id, [note]);
  }
  for (const group of groups.values()) group.sort(byWritten);
  return groups;
}

/**
 * **One paste is one row in the banner** (split-giant-note note, 2026-09-14).
 *
 * Eight rows saying "Reading the note…" over one gesture is a banner that has
 * stopped carrying information: the office pasted one thing and is waiting on
 * one thing. So the pending pieces of a group collapse into a line that counts
 * them, and the numbers come off the **whole** group rather than off the pending
 * ones — "3 of 8 read" is the sentence that answers "is this nearly done", and
 * counting only what is still in flight would answer a question nobody asked.
 *
 * The hour is the earliest start across the pieces still being read, which is
 * when the paste's reading began as far as anybody watching is concerned; a
 * group whose rows carry no start says the progress and nothing else, rather
 * than a zero o'clock it invented.
 */
function pasteReadingLine(groupId: string, pieces: readonly NoteEntry[]): ReadingLine | null {
  const reading = pieces.filter((piece) => piece.extraction_status === 'pending');
  if (reading.length === 0) return null;

  const starts = reading
    .map((piece) => piece.extraction_started_at)
    .filter((at): at is string => at !== null)
    .sort();
  const read = pieces.filter((piece) => piece.extraction_status === 'done').length;

  return {
    /* **The group, not a piece.** This row is one paste and it is the row's
       React key, so keying it on whichever piece happens still to be reading
       would give it a new identity every time one of them landed — the row would
       unmount and remount under the reader on every poll, and an `aria-live`
       region would announce it again each time. Nothing presses it (`canRetry`
       is false), so it is an identity and not a target. */
    input_id: groupId,
    tone: 'reading',
    label: READING_PASTE_LABEL,
    detail: pasteReadingDetail(read, pieces.length, formatClock(starts[0] ?? null)),
    /* The paste's **opening**, not the opening of whichever piece happens to
       still be reading: the office is being told which note this is, and the
       note they wrote begins at piece one. */
    note: noteLine(noteExcerpt(pieces[0]!.prose), pieces[0]!.kind),
    canRetry: false,
  };
}

/** A note the office typed, on the row it has always had. */
function plainReadingLine(note: NoteEntry): ReadingLine {
  /* A second attempt is a different sentence from a first one: "Reading the
     note…" on a note the office already pressed Retry on would read as though
     the press did nothing. `extraction_attempt` is 0 until the first attempt is
     queued, so 0 and 1 are both a first reading. */
  const again = note.extraction_attempt >= 2;
  const clock = formatClock(note.extraction_started_at);
  return {
    input_id: note.input_id,
    tone: 'reading',
    label: again ? 'Retrying…' : 'Reading the note…',
    detail: clock.length === 0 ? '' : again ? `Retrying since ${clock}` : `Started ${clock}`,
    note: noteLine(noteExcerpt(note.prose), note.kind),
    canRetry: false,
  };
}

/**
 * Every row the banner draws, built from the record and nothing else.
 *
 * Work under way leads, failures follow, each in the order the notes were
 * written: what is moving is what the reader is waiting on, and what stopped is
 * a decision to make afterwards.
 *
 * A note appears exactly once. The pending rows come from `notes` (the only
 * place a `pending` status lives) and the failed rows from `extraction_failures`
 * (which carries the excerpt and the attempt the note's own line would agree
 * with) — the two sets cannot overlap, because a note is one status at a time.
 *
 * **A paste is one gesture and is banded as one** (split-giant-note note,
 * 2026-09-14): the pieces of one paste that are still being read collapse into a
 * single counted row, while a piece that **stopped** keeps a row of its own with
 * its own Retry — because that is the level the office can act at, and re-reading
 * seven pieces to recover one is work nobody asked for and spend nobody
 * authorized.
 */
export function readingLines(record: SubjectRecord | undefined): ReadingLine[] {
  if (!record) return [];

  const groups = piecesByGroup(record.notes);
  /* One row per group, emitted where the group's first pending piece falls in
     the order, so a paste sits among the notes by when it was written. */
  const banded = new Set<string>();

  const reading: ReadingLine[] = [...record.notes]
    .filter((note) => note.extraction_status === 'pending')
    .sort(byWritten)
    .flatMap((note): ReadingLine[] => {
      if (note.split_group_id !== null) {
        if (banded.has(note.split_group_id)) return [];
        banded.add(note.split_group_id);
        const line = pasteReadingLine(
          note.split_group_id,
          groups.get(note.split_group_id) ?? [note],
        );
        return line ? [line] : [];
      }
      return [plainReadingLine(note)];
    });

  const failed: ReadingLine[] = [...record.extraction_failures].sort(byWritten).map((failure) => {
    const reason = failure.reason ?? NO_REASON;
    const again = failure.attempt >= 2;
    const clock = formatClock(failure.finished_at);
    return {
      input_id: failure.input_id,
      tone: 'failed' as const,
      /* **A piece is named by its place, not by its words.** The office typed
         one note, so an excerpt of piece 3 quotes something they never wrote as
         a unit; the number is what they can find it by. The excerpt is still on
         the row underneath, because it is what the reading was about. */
      label:
        failure.piece_seq === null
          ? 'The note could not be read'
          : pieceFailedLabel(failure.piece_seq),
      /* A second failure says so. The office pressed Retry and is owed the
         result of that press, not the same sentence it saw before pressing. The
         hour is dropped rather than faked when the row carries no finish. */
      detail: again
        ? clock.length === 0
          ? `Tried again and failed: ${reason}`
          : `Tried again at ${clock} and failed: ${reason}`
        : reason,
      note: noteLine(failure.excerpt, failure.kind),
      canRetry: true,
    };
  });

  return [...reading, ...failed];
}
